// "Video" demo of the SACEM assistant: a mock chat that types itself out
// on a loop, inside a browser frame. Reuses the app colors (useColors).
import { useEffect, useRef, useState } from 'react';
import { Box, Paper, Typography, LinearProgress, IconButton } from '@mui/material';
import PlayCircleIcon  from '@mui/icons-material/PlayCircle';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import { useColors } from '../../context/ThemeContext';

const REDUCE = typeof window !== 'undefined'
  && window.matchMedia
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const TURNS = [
  { role: 'assistant', text: "Bonjour ! Quel est le titre de l'œuvre à déclarer ?" },
  { role: 'user',      text: 'Lumière du matin' },
  { role: 'assistant', text: "Parfait. Qui en est l'auteur des paroles ?" },
  { role: 'user',      text: 'Marie Dupont' },
  { role: 'assistant', text: 'Et qui a composé la musique ?' },
  { role: 'user',      text: 'Léo Martin' },
  { role: 'assistant', text: 'Merci ! Votre feuillet SACEM est prêt à être généré. 🎉' },
];

export default function AssistantDemo() {
  const colors = useColors();
  const [shown, setShown] = useState([]);
  const [typing, setTyping] = useState('');
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const scrollRef = useRef(null);
  const timers = useRef([]);

  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };
  const wait = (ms, fn) => { const t = setTimeout(fn, ms); timers.current.push(t); };

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [shown, typing]);

  useEffect(() => {
    if (!playing) { clearTimers(); return; }
    clearTimers();
    if (idx >= TURNS.length) { wait(2600, () => { setShown([]); setTyping(''); setIdx(0); }); return; }
    const turn = TURNS[idx];
    if (turn.role === 'user') {
      wait(REDUCE ? 0 : 650, () => { setShown((s) => [...s, turn]); setIdx((i) => i + 1); });
    } else {
      let i = 0;
      const type = () => {
        i += 1;
        setTyping(turn.text.slice(0, i));
        if (i < turn.text.length) wait(REDUCE ? 0 : 26, type);
        else wait(520, () => { setShown((s) => [...s, turn]); setTyping(''); setIdx((n) => n + 1); });
      };
      wait(REDUCE ? 0 : 420, type);
    }
    return clearTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, playing]);

  const progress = Math.min(100, Math.round((idx / TURNS.length) * 100));

  const Bubble = ({ role, children }) => {
    const isUser = role === 'user';
    return (
      <Box sx={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start' }}>
        <Paper elevation={0} sx={{
          maxWidth: '78%', px: 2, py: 1.25, borderRadius: 3,
          borderBottomRightRadius: isUser ? 1 : 3,
          borderBottomLeftRadius:  isUser ? 3 : 1,
          background: isUser ? colors.gradientPrimary : colors.bgPaper,
          color: isUser ? '#fff' : colors.textPrimary,
          border: isUser ? 'none' : `1px solid ${colors.border}`,
          boxShadow: isUser ? 'none' : colors.shadow,
          whiteSpace: 'pre-wrap', fontSize: '0.95rem', lineHeight: 1.6,
        }}>{children}</Paper>
      </Box>
    );
  };

  return (
    <Box sx={{
      width: '100%', maxWidth: 520, mx: 'auto',
      border: `1px solid ${colors.border}`, borderRadius: 3, overflow: 'hidden',
      bgcolor: colors.bgPaper, boxShadow: colors.shadowLg,
    }}>
      {/* Browser chrome */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.75, py: 1.25, bgcolor: colors.bgMuted, borderBottom: `1px solid ${colors.border}` }}>
        {['#EF4444', '#F59E0B', '#10B981'].map((c) => <Box key={c} sx={{ width: 11, height: 11, borderRadius: '50%', bgcolor: c }} />)}
        <Typography sx={{ flex: 1, textAlign: 'center', fontSize: 12, color: colors.textMuted, fontFamily: 'ui-monospace, monospace' }}>publy-music.com/chat</Typography>
        <IconButton size="small" onClick={() => setPlaying((p) => !p)}
          aria-label={playing ? 'Mettre en pause la démo' : 'Lire la démo'}
          sx={{ color: colors.primaryLight, p: 0 }}>
          {playing ? <PauseCircleIcon fontSize="small" /> : <PlayCircleIcon fontSize="small" />}
        </IconButton>
      </Box>
      {/* Progress */}
      <Box sx={{ px: 1.75, pt: 1.25, pb: 0.75, bgcolor: colors.bgPaper }}>
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: colors.textSecondary, mb: 0.5 }}>Assistant SACEM</Typography>
        <LinearProgress variant="determinate" value={progress} sx={{
          height: 7, borderRadius: 999, bgcolor: colors.bgMuted,
          '& .MuiLinearProgress-bar': { borderRadius: 999, background: colors.gradientPrimary },
        }} />
      </Box>
      {/* Thread */}
      <Box ref={scrollRef} sx={{ height: 260, overflow: 'hidden', p: 1.75, display: 'flex', flexDirection: 'column', gap: 1.25, bgcolor: colors.bgDefault }}>
        {shown.map((m, i) => <Bubble key={i} role={m.role}>{m.text}</Bubble>)}
        {typing && <Bubble role="assistant">{typing}<span style={{ opacity: 0.5 }}>▋</span></Bubble>}
      </Box>
    </Box>
  );
}
